import React from 'react';
import {
  Box,
  Button,
  Divider,
  FormHelperText,
  IconButton,
  InputAdornment,
  List,
  ListItem,
  ListItemText,
  Paper,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import LightBulb from '../../assets/images/light-bulb.svg';
import Team from '../../assets/images/team.svg';
import Partnership from '../../assets/images/partnership.svg';
import Megaphone from '../../assets/images/megaphone.svg';
import Ktng from '../../assets/images/ktng.svg';
import { YellowBoxBottom } from './Home';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';

const IconFeatureBlock = ({
  icon,
  heading,
  description,
  bulletPoints,
  iconNewLine,
  sx,
}) => (
  <Stack
    direction={iconNewLine ? 'column' : 'row'}
    spacing={2}
    alignItems="flex-start"
    sx={sx}
  >
    <Box
      component="img"
      src={icon}
      alt={heading}
      sx={{ width: 36, height: 36, mt: 0.5 }}
    />
    <Box>
      <Typography fontWeight="bold">{heading}</Typography>
      {description && (
        <Typography variant="body2" mt={0.5} sx={{ whiteSpace: 'pre-line' }}>
          {description}
        </Typography>
      )}
      {bulletPoints && (
        <List dense disablePadding sx={{ mt: 1 }}>
          {bulletPoints.map((point, index) => (
            <ListItem key={index} disableGutters disablePadding sx={{ pl: 0 }}>
              <ListItemText
                primary={`• ${point}`}
                sx={{ '.MuiTypography-root': { fontWeight: '500' } }}
              />
            </ListItem>
          ))}
        </List>
      )}
    </Box>
  </Stack>
);

const ReferralHero = ({
  userName = 'Ravi',
  referralLink = 'https://example.com/invite',
}) => {
  return (
    <Box
      sx={{ height: '570px', width: '1200px', transform: 'translateX(-12%)' }}
    >
      <Box
        sx={{
          position: 'relative',
          bgcolor: '#FBF1E7',
          pl: 3,
          display: 'flex',
          flexDirection: 'row',
          alignItems: 'stretch',
          justifyContent: 'space-between',
          height: '421px',
        }}
      >
        {/* Left Section */}
        <Box sx={{ maxWidth: 500, zIndex: 1, mt: 3, p: 5 }}>
          <Typography
            variant="h2"
            fontWeight="bold"
            fontSize="40"
            color="#454545"
          >
            <Box component="span" color="#D78100">
              {userName},
            </Box>{' '}
            Refer & Earn Free Questions
          </Typography>

          <Typography mt={2} variant="homePageBody" paragraph color="#454545">
            Earn Up to 3 Free Questions by Referring Friends
          </Typography>

          <Typography
            mt={3}
            variant="homePageBody"
            paragraph
            color="#454545"
            fontSize="14px"
          >
            *Terms and conditions apply
          </Typography>
        </Box>

        {/* Right Side Image Placeholder */}
        <Box
          sx={{
            flex: 1,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'flex-end',
            height: '100%',
            width: '100%',
          }}
        >
          <Box
            component="img"
            src={Ktng}
            alt="Referral"
            sx={{
              height: '100%',
              objectFit: 'cover',
              width: '100%',
            }}
          />
        </Box>

        {/* Overlapping Email Box */}
        <Paper
          elevation={1}
          sx={{
            position: 'absolute',
            bgcolor: 'background.default',
            '&:hover .hover-blur': {
              filter: 'blur(6px)',
              transform: 'scale(1.02)',
            },
            '&:hover .overlay': {
              opacity: 1,
            },
            top: 300,
            left: { xs: 20, md: 64 },
            right: { xs: 20, md: 'auto' },
            width: { xs: 'calc(100% - 40px)', md: 450 },
            p: 3,
            zIndex: 2,
          }}
        >
          {/* Overlay text */}
          <Box
            className="overlay"
            sx={{
              position: 'absolute',
              inset: 0,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              opacity: 0,
              transition: 'opacity .25s ease',
              bgcolor: 'rgba(0,0,0,0.2)',
              color: '#fff',
              borderRadius: '4px',
              fontWeight: 700,
              fontSize: '1.25rem',
              textAlign: 'center',
              textShadow: '0 2px 8px rgba(0,0,0,0.5)',
              pointerEvents: 'none', // clicks pass through
            }}
          >
            🚀 Launching Soon
          </Box>
          <Box
            className="hover-blur"
            sx={{ transition: 'filter .3s ease, transform .3s ease' }}
          >
            <Typography
              variant="homePageBody"
              paragraph
              fontWeight="bold"
              mb={1}
            >
              Invite Friend through email
            </Typography>
            <Stack
              direction="row"
              spacing={1}
              mb={2}
              justifyContent="space-between"
            >
              <Stack direction="column" spacing={1} width="100%">
                <TextField
                  placeholder="Add Email addresses"
                  fullWidth
                  size="small"
                  disabled
                />
                <FormHelperText>
                  <Typography variant="caption">
                    Separate emails with commas
                  </Typography>
                </FormHelperText>
              </Stack>
              <Button
                variant="contained"
                size="small"
                color="secondary"
                sx={{
                  px: 3,
                  height: '32px',
                }}
                disabled
              >
                Send
              </Button>
            </Stack>

            <Typography variant="body2" mb={1}>
              or share your personal referral link
            </Typography>

            <TextField
              fullWidth
              size="small"
              disabled
              placeholder={referralLink}
              InputProps={{
                readOnly: true,
                endAdornment: (
                  <InputAdornment position="end">
                    <IconButton
                      onClick={() =>
                        navigator.clipboard.writeText(referralLink)
                      }
                      disabled
                    >
                      <ContentCopyIcon fontSize="small" />
                    </IconButton>
                  </InputAdornment>
                ),
              }}
            />
          </Box>
        </Paper>
      </Box>
    </Box>
  );
};

const MyReferral = () => {
  return (
    <Box>
      <Typography variant="homePageBody" paragraph>
        At <span style={{ fontFamily: 'SpaceGrotesk' }}>KaliyugAi</span>, your
        trust means everything. When you refer a friend, you're not just sharing
        a tool you're helping us build a more ethical, accurate, and accessible
        astrology ecosystem. As a thank you, you’ll receive free questions for
        every three referral. Let’s grow this movement together one
        recommendation, one connection, and one accurate prediction at a time.
      </Typography>

      <Box
        sx={{ marginTop: '100px', marginBottom: '100px' }}
        height="fit-content"
      >
        <ReferralHero />
      </Box>

      <Typography variant="overline" color="#F3A77F" fontWeight="bold">
        FROM REFERRAL TO REWARD
      </Typography>
      <Typography
        variant="h4"
        fontWeight="bold"
        mt={1}
        sx={{ marginBottom: '36px' }}
      >
        It’s Easy to Earn with Referrals
      </Typography>

      {/* Top Two Steps */}
      <Stack direction={'row'} spacing={4} mb={5}>
        <Box sx={{ width: '45%' }}>
          <IconFeatureBlock
            icon={Team}
            heading="Refer 3 Friends"
            bulletPoints={[
              'When each of your 3 referred friends buys at least 10 tokens (Seeker’s Light Plan), you will get 1 free question (worth 10 tokens) to ask!',
            ]}
            iconNewLine={true}
          />
        </Box>
        <Box sx={{ width: '55%' }}>
          <IconFeatureBlock
            icon={Partnership}
            heading="Go Further!"
            bulletPoints={[
              `Refer 4 more friends (for a total of 7 friends).`,
              `Each friend buys 10 tokens from separate accounts.`,
              `Unlock 2 additional questions (total of 3 free questions) and extra perks!`,
            ]}
            iconNewLine={true}
          />
        </Box>
      </Stack>

      {/* Rewards */}
      <Box sx={{ marginTop: '100px' }}>
        <IconFeatureBlock
          icon={LightBulb}
          heading="What You Get!"
          bulletPoints={[
            '1 Free Question after 3 referrals',
            '2 More Free Questions after referring 4 additional people (total of 3 questions)',
            'Exclusive Perks after reaching the 6-referral milestone!',
          ]}
        />
      </Box>

      {/* Why Refer */}
      <Box sx={{ marginTop: '80px' }}>
        <IconFeatureBlock
          icon={Megaphone}
          heading="Why should you refer"
          bulletPoints={[
            'Help your friends discover astrology insights.',
            'Earn rewards with every referral.',
            'The more you refer, the more questions and perks you unlock, it’s that simple!',
          ]}
        />
      </Box>

      <YellowBoxBottom>
        <Typography variant="h2" fontWeight={600}>
          Refer & Rise Together
        </Typography>
        <Typography variant="homePageBody" paragraph px={6}>
          Share <span style={{ fontFamily: 'SpaceGrotesk' }}>KaliyugAi</span>{' '}
          with your circle help others find clarity, and earn free questions as
          a thank-you. Your growth fuels ours.
        </Typography>
      </YellowBoxBottom>
    </Box>
  );
};

export default MyReferral;
